import vmClient from 'app/services/vmClient';
import { Entity, EntityStringId, IEntity, IEntityType } from 'app/types/entity';
import { TranslationsForEntity, ALL_TRANSLATIONS } from 'app/constants/schemas/translations';

export interface IBrandDomain extends IEntity {
  attributes: {
    /**
     * The id for an instance of this entity.
     */
    id: EntityStringId;
    /**
     * The Production source of truth for this domain's hostname.
     * c.f. {@link envUri} the env aware mutation of the hostname.
     */
    hostname: string;
    /**
     * Path string to prefix all routes on this domain. For example `/app`.
     */
    pathPrefix: string;
    /**
     * A mutated version of the {@link hostname} which is aware of the current development environment.
     * For example on localhost, this value might be `https://engoo-com.devdomain.name:3000/app`. On a branch it might
     * be `https://branch.dev.engoo.com/app`, and on production it will be https://engoo.com/app.
     */
    readonly envUri: string;
    /**
     * `BrandDomain`s are directly linked to {@link Brand} and it is always the case that a brand has a
     * single `BrandDomain` marked as `default`, indicating that that domain should be treated as canonical
     * for the parent `Brand`.
     */
    default: boolean;
    /**
     * On branch servers, should the envUri be calculated following the `branch.dev.foobar.com` pattern or
     * `branch.foobar-com.orgs.eikaiwa.dev`. This will be set by organization that have correctly configured
     * branch server integrations.
     */
    devSubdomain: boolean;
    /**
     * Is this domain considered deprecated. For example `app.engoo.com` might be considered deprecated.
     */
    deprecated: boolean
  };
}

export type BrandDomain = Entity<IBrandDomain>;
// defineClass is a class factory, normally classes create a runtime variable and identifier in the type namespace.
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const BrandDomain: IEntityType<IBrandDomain> = vmClient.defineClass({
  typeName: 'BrandDomain',
  version: 2,
  persisted: true,
  attributes: {
    hostname: {
      type: 'string',
      notNull: true,
    },
    pathPrefix: {
      type: 'string',
      notNull: true,
    },
    envUri: {
      type: 'string',
      notNull: true,
    },
    default: {
      type: 'boolean',
      notNull: true,
    },
    devSubdomain: {
      type: 'boolean',
      notNull: true,
    },
    deprecated: {
      type: 'boolean',
      notNull: true,
    },
  },
});

export const BrandDomainTranslations: Partial<TranslationsForEntity<IBrandDomain>> = {
  id: {
    getLabel: (t) => t('entity-fields.BrandDomain.id.label'),
  },
  hostname: {
    getLabel: (t) => t('entity-fields.BrandDomain.hostname.label'),
    getDescription: (t) => t('entity-fields.BrandDomain.hostname.description'),
  },
  pathPrefix: {
    getLabel: (t) => t('entity-fields.BrandDomain.pathPrefix.label'),
    getDescription: (t) => t('entity-fields.BrandDomain.pathPrefix.description'),
  },
  envUri: {
    getLabel: (t) => t('entity-fields.BrandDomain.envUri.label'),
    getDescription: (t) => t('entity-fields.BrandDomain.envUri.description'),
  },
  default: {
    getLabel: (t) => '',
    getInlineLabel: (t) => t('entity-fields.BrandDomain.default.inline-label'),
    getDescription: (t) => t('entity-fields.BrandDomain.default.description'),
  },
  devSubdomain: {
    getLabel: (t) => '',
    getInlineLabel: (t) => t('entity-fields.BrandDomain.devSubdomain.inline-label'),
    getDescription: (t) => t('entity-fields.BrandDomain.devSubdomain.description'),
  },
  deprecated: {
    getLabel: (t) => '',
    getInlineLabel: (t) => t('entity-fields.BrandDomain.deprecated.inline-label'),
    getDescription: (t) => t('entity-fields.BrandDomain.deprecated.description'),
  },
};

ALL_TRANSLATIONS.set(BrandDomain, BrandDomainTranslations);
